import React, { useRef } from 'react';
import { MultiColumnEntry, MultiColumnEntryProps } from '../MultiColumnEntry/MultiColumnEntry';
import { styles } from './MultiColumnBlock.css';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { ContentThemePalette } from '../Contentful/transformers/ThemeTransformer';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';

export interface MultiColumnBlockProps {
  entries: Array<Omit<MultiColumnEntryProps, 'alignment'>>;
  desktopColumns: 2 | 3 | 4 | 5;
  mobileFormat: 'Stack' | 'Double' | 'Carousel';
  alignment: 'Left' | 'Centred';
  theme?: ContentThemePalette;
  heading?: string;
  enableContainerBreakpoints?: boolean;
}

export const MultiColumnBlock: React.FC<MultiColumnBlockProps> = ({
  heading,
  entries,
  desktopColumns = 3,
  mobileFormat,
  alignment,
  theme,
  enableContainerBreakpoints = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = enableContainerBreakpoints ? ref : undefined;
  const isDesktopView = useBreakpoint('md', 'up', containerRef);
  const isLargeDesktopView = useBreakpoint('lg', 'up', containerRef);
  const isTabletView = isDesktopView && !isLargeDesktopView;

  const isCentred = alignment === 'Centred';
  // We need to set the number of columns to 4 for tablets when the desktopColumns is 5
  const desktopCount = isTabletView && desktopColumns === 5 ? 4 : desktopColumns;
  const mobileCount = mobileFormat === 'Double' ? 2 : 1;
  const columns = isDesktopView ? desktopCount : mobileCount;

  const items = entries?.map((entry, index) => <MultiColumnEntry key={index} {...entry} alignment={alignment} />);

  return (
    <>
      {heading ? <CalloutFont as="h2">{heading}</CalloutFont> : null}
      <div
        className={styles.container({ isCentred })}
        style={{
          backgroundColor: theme?.background,
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
        }}
        ref={ref}
      >
        {!isDesktopView && mobileFormat === 'Carousel' ? (
          <Carousel slidesPerView={1.2} showPagination={false} fullBleed fullHeightItems showArrows={false}>
            {items}
          </Carousel>
        ) : (
          items
        )}
      </div>
    </>
  );
};
